import { createFileRoute } from "@tanstack/react-router";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/sprinter-exec-amber.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/limo-style-sprinter-party-bus-lounge-bar.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryA from "@/assets/sprinter-exec-green.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryB from "@/assets/sprinter-limo-3.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryC from "@/assets/luxury-suburban-suv-captain-seats-atlanta.jpg?format=webp&quality=72&w=1400&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/private-chauffeur-hourly-rates-atlanta";
const PAGE_TITLE = "Hourly Chauffeur Service Atlanta | Rates From $250/Hour";
const PAGE_DESC =
  "What a private chauffeur costs per hour in Atlanta: from $250/hour, your chauffeur waits at every stop, and your quote is in writing before you book. Rated 5.0 on Google.";

const faqs = [
  {
    q: "How much is a private chauffeur per hour in Atlanta?",
    a: "From $250 per hour for a chauffeured Mercedes-Benz Sprinter, with Suburban SUVs quoted upon request. The rate covers the vehicle, the chauffeur, fuel, staging, and unlimited stops inside your block, and is confirmed in writing before you reserve.",
  },
  {
    q: "What does 'as-directed' actually mean?",
    a: "The vehicle and chauffeur are yours for the block and go where you say, in whatever order the evening takes. No re-booking between stops, no waiting for a car to come back, no explaining the plan twice.",
  },
  {
    q: "How long can I book a chauffeur for?",
    a: "Hourly service has a 4-hour minimum, and from there as long as you need, from an evening to a multi-day itinerary. Custom quotes are given upon request for any block of time, and airport and private-terminal transfers are quoted flat.",
  },
  {
    q: "How is overtime handled?",
    a: "If the night runs long, the chauffeur stays and time bills in increments at your confirmed rate. You are told before it accrues. Nobody is hurried out of a restaurant because a block expired.",
  },
  {
    q: "Can I keep the same chauffeur every time?",
    a: "For standing clients, yes, that is the entire point of an account. Preferences, routes, gate codes, and the temperature of the cabin stop being something you communicate and start being something that is simply true when the door opens.",
  },
  {
    q: "Do you handle multi-day and travelling arrangements?",
    a: "Yes. Multi-day coverage in Atlanta, out-of-market days to Savannah, Chattanooga, or Lake Oconee, and standing weekly arrangements are all quoted as planned blocks with a fixed written figure.",
  },
];

export const Route = createFileRoute("/private-chauffeur-hourly-rates-atlanta")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "private chauffeur hourly rates atlanta, hourly car service atlanta, as directed chauffeur atlanta, hourly sprinter rental atlanta, private driver cost atlanta",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: heroImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
      { name: "twitter:image", content: heroImage },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Hourly Private Chauffeur Service",
            image: heroImage,
            description: PAGE_DESC,
            serviceType: "Hourly Private Chauffeur Service",
            areaServed: [
              { type: "City", name: "Atlanta" },
              { type: "City", name: "Buckhead" },
              { type: "City", name: "Alpharetta" },
              { type: "City", name: "Sandy Springs" },
            ],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="Hourly • As-Directed • From $250 / Hour"
      h1="Private Chauffeur Hourly Rates in Atlanta"
      intro="Your day has several stops, and you don't want to keep arranging rides. Reserve a vehicle and chauffeur for your hours. Mercedes-Benz Sprinters start at $250/hour, with a custom written quote."
      heroImage={heroImage}
      heroAlt="Chauffeured Mercedes-Benz Sprinter cabin reserved by the hour in Atlanta"
      midImage={midImage}
      midAlt="Lounge seating inside an Atlanta Mercedes-Benz Sprinter held on an hourly block"
      sectionOneTitle="Your Chauffeur Stays With You"
      sectionOneBody={[
        "Dinner, a meeting or an extra stop: your vehicle stays with you throughout the reserved block. Your chauffeur waits while you enjoy your plans.",
        "Mercedes-Benz Sprinters start at $250/hour, reserved only for your group. Text me your date and hours for a written quote.",
        "We also arrange recurring travel for families, executives and visiting guests. Tell me your routine and preferences so we can plan around them.",
      ]}
      bulletsTitle="What an Hourly Block Includes"
      bulletsIntro="One vehicle and chauffeur for your reserved hours."
      bullets={[
        "Exclusive vehicle and chauffeur for the full block",
        "Unlimited stops, in any order, changed on the fly",
        "Chauffeur waits on site, never dismissed and re-summoned",
        "Fuel, detailing, staging, and positioning time",
        "Chilled water, stemware, and cabin prepared to preference",
        "Direct contact with your chauffeur throughout the block",
        "24-hour support answered by a person",
        "Total discretion about who travelled, where, and with whom",
      ]}
      coverageTitle="Where Hourly Service Runs"
      coverageIntro="Metro Atlanta as standard; out-of-market days quoted flat."
      coverage={[
        "Buckhead",
        "Midtown & Downtown",
        "Sandy Springs & Dunwoody",
        "Alpharetta & Milton",
        "Roswell & Johns Creek",
        "Brookhaven & Chamblee",
        "Vinings & Smyrna",
        "Decatur & Druid Hills",
        "Hartsfield-Jackson (ATL)",
        "PDK, FTY & RYY",
        "Lake Oconee",
        "North Georgia wine country",
      ]}
      routeTable={{
        title: "Hourly Blocks and What They're Used For",
        intro:
          "Typical block lengths our clients reserve. Exact figures are issued in writing and depend on vehicle, date, and duration.",
        rows: [
          {
            route: "Dinner and an evening out",
            distance: "Sprinter or SUV",
            time: "4–5 hours",
            notes: "Buckhead, Midtown, and Westside restaurants with the vehicle waiting between stops.",
          },
          {
            route: "Concert or stadium night",
            distance: "Sprinter",
            time: "5–6 hours",
            notes: "Pre-arranged staging so departure isn't decided by 20,000 other people.",
          },
          {
            route: "Executive day, as-directed",
            distance: "Executive Sprinter",
            time: "8–10 hours",
            notes: "Meetings, lunch, airport. One chauffeur across the whole day.",
          },
          {
            route: "Wedding day coverage",
            distance: "Sprinter Limousine",
            time: "6–10 hours",
            notes: "Planned to the timeline and quoted as one block.",
          },
          {
            route: "Wine country day trip",
            distance: "Sprinter",
            time: "8–10 hours",
            notes: "Dahlonega and North Georgia; chauffeur stays with the group all day.",
          },
          {
            route: "Multi-day / visiting principal",
            distance: "Any vehicle",
            time: "Daily block",
            notes: "Same chauffeur throughout the visit. Billed as a planned arrangement.",
          },
          {
            route: "Standing weekly arrangement",
            distance: "Any vehicle",
            time: "Recurring block",
            notes: "Preferences on file, priority dates, consolidated monthly billing.",
          },
        ],
        footnote:
          "Custom quotes are given upon request. Overtime bills in increments at your confirmed rate and is always disclosed before it accrues.",
      }}
      detailSections={[
        {
          title: "Why Hourly Costs More Than a Point-to-Point Ride",
          body: [
            "Because you are paying for availability rather than distance. A transfer uses the vehicle for forty minutes; a block reserves it for the night, including the two hours it spends waiting in a garage with the engine off and the chauffeur awake.",
            "That waiting is the product. Every client who has stood outside a venue at midnight watching a surge multiplier climb understands exactly what they were buying and did not.",
          ],
        },
        {
          title: "Discretion as a Line Item",
          list: [
            "Chauffeurs vetted for judgment, not just a clean record.",
            "No names, itineraries, or photographs discussed, internally or otherwise.",
            "Side and service entrances arranged in advance where privacy matters.",
            "NDAs signed on request for talent, executive, and family office work.",
            "The same small bench of chauffeurs, so fewer people know anything at all.",
          ],
        },
        {
          title: "Who This Is Actually For",
          body: [
            "Clients who have already decided that their time, their guests, and their privacy are worth more than the difference between $150 and $250 an hour. Family offices, visiting principals, touring talent, wedding hosts who only do this once, and Atlanta households that have simply stopped driving themselves at night.",
            "If the rate is the obstacle, we will say so kindly and there are operators in the city who will be happy to help. If it isn't, the only question left is whether your date is still open, and increasingly, it isn't.",
          ],
        },
      ]}
      gallery={{
        title: "Reserved by the Hour",
        intro: "The cabins our hourly clients keep off the board for the evening.",
        images: [
          { src: galleryA, alt: "Executive Sprinter interior reserved hourly in Atlanta", caption: "Executive Sprinter, up to 13" },
          { src: galleryB, alt: "Mercedes-Benz Sprinter Limousine cabin reserved by the hour in Atlanta", caption: "Sprinter Limousine, up to 14" },
          { src: galleryC, alt: "Suburban SUV interior for discreet hourly Atlanta service", caption: "Suburban SUV, up to 6 (captain seats) or 7 (bench seats)" },
        ],
      }}
      faqs={faqs}
      faqTitle="Hourly Chauffeur Rate FAQs"
      relatedTitle="Related Pricing Pages"
      related={[
        { to: "/sprinter-limo-cost-atlanta", title: "Sprinter Limo Cost", desc: "The full answer on Sprinter limousine pricing." },
        { to: "/luxury-car-service-rates-atlanta", title: "Luxury Car Service Rates", desc: "Rate structure, custom quotes, and inclusions." },
        { to: "/chauffeur-service-atlanta", title: "Private Chauffeur Service", desc: "How the service itself is run." },
      ]}
      ctaTitle="Text Me the Hours You Need"
      ctaBody="Tell me your date, pickup, stops and how long you'd like the vehicle. I'll check availability personally and send your quote in writing."
    />
  );
}
